'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useLanguage } from '@/lib/LanguageContext';

const Navbar = dynamic(() => import('@/app/components/Navbar'), { ssr: false });
const Footer = dynamic(() => import('@/app/components/Footer'), { ssr: false });

interface AboutClientProps {
  content: any;
  nonce?: string;
}

const revealVariant = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay: i * 0.12 },
  }),
};

export default function AboutClient({ content }: AboutClientProps) {
  const { lang, setLang } = useLanguage();
  const [activeLang, setActiveLang] = useState<'en' | 'si'>(lang === 'si' ? 'si' : 'en');

  useEffect(() => {
    setActiveLang(lang === 'si' ? 'si' : 'en');
  }, [lang]);

  const handleLangToggle = (newLang: 'en' | 'si') => {
    setActiveLang(newLang);
    setLang(newLang === 'si' ? 'si' : 'en');
  };

  const isSi = activeLang === 'si';

  const chapters = [
    { num: '01', data: content.chapter1 },
    { num: '02', data: content.chapter2 },
    { num: '03', data: content.chapter3 },
    { num: '04', data: content.chapter4 },
    { num: '05', data: content.chapter5 },
    { num: '06', data: content.chapter6 },
  ];

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: '#F6FBFF' }}
    >

      <Navbar locale={activeLang === 'si' ? 'si' : 'en'} />

      {/* Premium Hero Banner */}
      <div className="relative min-h-[38vh] md:min-h-[42vh] flex items-center justify-center overflow-hidden pt-36 pb-24 w-full">
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 46.8% 109%, #BFDEFF 0%, #659EDA 99%)',
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 88%, 50% 100%, 0% 88%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(hsla(0,0%,100%,0.15) 1px, transparent 1px), linear-gradient(90deg, hsla(0,0%,100%,0.15) 1px, transparent 1px)',
            backgroundSize: '100px 100px',
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 88%, 50% 100%, 0% 88%)',
          }}
        />

        <div className="relative z-10 text-center space-y-4 max-w-2xl mx-auto px-4 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/35 bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm shadow-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {isSi ? 'අප ගැන' : 'About Us'}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`font-display uppercase tracking-tight leading-none text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)] ${
              isSi ? 'text-3xl md:text-4xl lg:text-5xl font-black font-sinhala-title' : 'text-4xl md:text-5xl lg:text-6xl text-white'
            }`}
          >
            {isSi ? content.hero.h1_si : content.hero.h1_en}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`text-white/85 font-medium tracking-wide max-w-xl mx-auto ${
              isSi ? 'font-sinhala text-sm sm:text-base' : 'text-xs sm:text-sm uppercase'
            }`}
          >
            {isSi ? content.hero.tagline_si : content.hero.tagline_en}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex flex-col items-center text-center bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-5 rounded-[24px] shadow-sm mt-6 hover:bg-white/15 transition-all duration-300"
          >
            <img
              src="/assets/baasil@0.5x.webp"
              alt="Baasil Sabry"
              className="w-32 h-32 md:w-36 md:h-36 rounded-full border-4 border-white/40 object-cover shadow-md mb-4"
            />
            {/* Row 2: Name and other details */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-base font-bold text-white font-mono tracking-wide">
                  Baasil Sabry
                </span>
                <svg
                  className="w-3.5 h-3.5 text-blue-400 fill-current"
                  viewBox="0 0 24 24"
                >
                  <path d="M23 12l-2.44-2.78.34-3.68-3.61-.82-1.89-3.18L12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68L23 12zm-13 5l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                </svg>
              </div>
              <span className="text-xs text-white/70 font-semibold font-mono uppercase tracking-wider">
                Garena V Badge Partner
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center max-w-4xl w-full mx-auto px-4 -mt-12 pb-24 relative z-20">
        <motion.div
          className="w-full space-y-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          {/* Navigation & Language Picker Bar */}
          <div className="w-full flex items-center justify-between bg-white rounded-[20px] px-5 py-3 shadow-sm border-[4px] border-white drop-shadow-sm">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-950 font-bold uppercase tracking-wider text-xs font-mono"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isSi ? 'ආපසු' : 'Back'}</span>
            </Link>

            <div className="flex items-center gap-1 bg-slate-50 border border-slate-100 rounded-xl p-0.5">
              <button
                onClick={() => handleLangToggle('en')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                  !isSi ? 'bg-[#548DC8] text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => handleLangToggle('si')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                  isSi ? 'bg-[#548DC8] text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                සිංහල
              </button>
            </div>
          </div>

          {/* Chapters Section */}
          <div className="space-y-6">
            {chapters.map((chapter, chIndex) => {
              const chData = chapter.data;
              const paragraphs = isSi && chData.si && chData.si.length > 0 ? chData.si : chData.en;

              return (
                <div
                  key={chapter.num}
                  className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border-[4px] border-white drop-shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-1/4 flex md:flex-col justify-between border-b md:border-b-0 md:border-r border-slate-100 pb-4 md:pb-0 md:pr-6">
                      <div>
                        <span className="text-xs font-mono font-bold tracking-widest text-slate-400 uppercase">
                          {chData.label} {chapter.num}
                        </span>
                      </div>
                      <div className="text-3xl md:mt-4">{chData.icon}</div>
                    </div>
                    <div className="md:w-3/4">
                      <h2
                        className={`text-xl md:text-2xl text-slate-800 font-bold mb-4 ${
                          isSi ? 'font-sinhala-title' : 'font-display uppercase tracking-wide'
                        }`}
                      >
                        {isSi ? chData.h2_si : chData.h2_en}
                      </h2>
                      <div className="space-y-4">
                        {paragraphs.map((p: string, pIndex: number) => (
                          <p
                            key={pIndex}
                            className={`text-slate-600 text-sm leading-relaxed ${
                              isSi ? 'font-sinhala text-slate-700 font-medium' : 'font-mono'
                            }`}
                          >
                            {p}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stats Grid Section */}
          <div className="bg-white rounded-[24px] p-8 md:p-12 shadow-sm border-[4px] border-white drop-shadow-sm">
            <h3
              className={`text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-8 text-center ${
                isSi ? 'font-sinhala font-bold' : ''
              }`}
            >
              {isSi ? content.stats.h2_si : content.stats.h2_en}
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-10 gap-x-6">
              {content.stats.items.map((stat: any, index: number) => (
                <div
                  key={index}
                  className="text-center p-4 rounded-2xl hover:bg-slate-50/50 transition-colors duration-200"
                >
                  <div className="text-3xl md:text-4xl font-bold tracking-tight text-[#548DC8] mb-2 font-mono">
                    {stat.number}
                  </div>
                  <div
                    className={`text-xs text-slate-500 font-semibold tracking-wide ${
                      isSi ? 'font-sinhala' : 'font-mono uppercase'
                    }`}
                  >
                    {isSi ? stat.label_si : stat.label_en}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* V-Badge Explainer Section */}
          <div className="bg-white rounded-[24px] p-6 md:p-10 shadow-sm border-[4px] border-white drop-shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -mr-16 -mt-16 pointer-events-none" />
            <h3
              className={`text-xl md:text-2xl text-slate-800 font-bold mb-4 flex items-center gap-2 ${
                isSi ? 'font-sinhala-title' : 'font-display uppercase tracking-wide'
              }`}
            >
              <span className="text-amber-500">✅</span>
              {isSi ? content.vbadge.h2_si : content.vbadge.h2_en}
            </h3>

            <div className="space-y-4 mb-8">
              {(isSi ? content.vbadge.si : content.vbadge.en).map((p: string, idx: number) => (
                <p
                  key={idx}
                  className={`text-slate-600 text-sm leading-relaxed ${
                    isSi ? 'font-sinhala font-medium text-slate-700' : 'font-mono'
                  }`}
                >
                  {p}
                </p>
              ))}
            </div>

            <a
              href={content.vbadge.cta_href}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-widest text-[#548DC8] hover:text-[#4377B0] transition-colors border-b border-[#548DC8]/30 hover:border-[#548DC8] pb-1 ${
                isSi ? 'font-sinhala' : ''
              }`}
            >
              <span>{isSi ? content.vbadge.cta_si : content.vbadge.cta_en}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Social Channels Directory */}
          <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border-[4px] border-white drop-shadow-sm">
            <div className="border-b border-slate-100 pb-4 mb-8">
              <h3
                className={`text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 ${
                  isSi ? 'font-sinhala font-bold' : ''
                }`}
              >
                {isSi ? content.social.h2_si : content.social.h2_en}
              </h3>
              <p className={`text-slate-500 text-xs ${isSi ? 'font-sinhala' : 'font-mono'}`}>
                {isSi ? content.social.subtitle_si : content.social.subtitle_en}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.social.channels.map((channel: any, idx: number) => (
                <div
                  key={idx}
                  className="flex flex-col justify-between p-5 rounded-2xl bg-slate-50/50 border border-slate-100/60 hover:bg-slate-50 hover:shadow-sm transition-all duration-200"
                >
                  <div>
                    <div className="flex items-baseline justify-between mb-3">
                      <h4 className="font-bold text-slate-800 text-sm font-mono uppercase tracking-wide">
                        {channel.platform}
                      </h4>
                      {channel.followers && (
                        <span className="text-[10px] font-mono text-slate-400 bg-white px-2 py-0.5 rounded-full border border-slate-100">
                          {channel.followers}
                        </span>
                      )}
                    </div>
                    <p
                      className={`text-slate-500 text-sm leading-relaxed mb-4 ${
                        isSi ? 'font-sinhala text-slate-600 font-medium' : 'font-mono'
                      }`}
                    >
                      {isSi ? channel.desc_si : channel.desc_en}
                    </p>
                  </div>
                  <a
                    href={channel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="self-start text-xs font-mono font-bold text-[#548DC8] hover:text-[#4377B0] border-b border-transparent hover:border-[#548DC8] pb-0.5 transition-all"
                  >
                    {channel.cta}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Why Baasil Topup is Different (Trust Section) */}
          <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border-[4px] border-white drop-shadow-sm">
            <div className="border-b border-slate-100 pb-4 mb-8">
              <h3
                className={`text-xs font-mono uppercase tracking-[0.2em] text-slate-400 ${
                  isSi ? 'font-sinhala font-bold' : ''
                }`}
              >
                {isSi ? content.trust.h2_si : content.trust.h2_en}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.trust.items.map((item: any, idx: number) => (
                <div
                  key={idx}
                  className="flex gap-4 p-4 rounded-xl hover:bg-slate-50/55 transition-colors duration-200"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-50 text-[#548DC8] font-bold text-lg border border-blue-100">
                    {item.icon}
                  </div>
                  <div className="flex-1">
                    <h4
                      className={`text-sm font-bold text-slate-800 mb-1 font-mono uppercase tracking-wide ${
                        isSi ? 'font-sinhala-title text-base' : ''
                      }`}
                    >
                      {isSi ? item.title_si : item.title_en}
                    </h4>
                    <p
                      className={`text-slate-500 text-xs leading-relaxed ${
                        isSi ? 'font-sinhala text-slate-600 font-medium' : 'font-mono'
                      }`}
                    >
                      {isSi ? item.desc_si : item.desc_en}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Closing Statement */}
          <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border-[4px] border-white drop-shadow-sm border-l-[6px] border-l-[#548DC8]">
            <h3
              className={`text-lg font-bold text-slate-800 mb-3 ${
                isSi ? 'font-sinhala-title text-xl' : 'font-display uppercase tracking-wide'
              }`}
            >
              {isSi ? content.closing.h2_si : content.closing.h2_en}
            </h3>
            <p
              className={`text-slate-600 text-sm leading-relaxed ${
                isSi ? 'font-sinhala text-slate-700 font-medium' : 'font-mono'
              }`}
            >
              {isSi ? content.closing.si : content.closing.en}
            </p>
          </div>

          {/* Contact CTA */}
          <div className="bg-white rounded-[24px] p-6 md:p-10 shadow-sm border-[4px] border-white drop-shadow-sm">
            <div className="mb-6">
              <h3
                className={`text-xl md:text-2xl text-slate-800 font-bold mb-2 tracking-tight ${
                  isSi ? 'font-sinhala-title' : 'font-display uppercase'
                }`}
              >
                {isSi ? content.cta.h2_si : content.cta.h2_en}
              </h3>
              <p
                className={`text-slate-500 text-sm leading-relaxed ${
                  isSi ? 'font-sinhala font-medium' : 'font-mono'
                }`}
              >
                {isSi ? content.cta.desc_si : content.cta.desc_en}
              </p>
            </div>

            {/* Clean Contact Details List */}
            <div className="flex flex-col gap-2.5 mb-10 text-sm font-mono text-slate-500 bg-slate-50 p-5 rounded-2xl border border-slate-100">
              {content.cta.contacts.map((contact: any, index: number) => (
                <div key={index} className="flex gap-2">
                  <span className="text-slate-400">{contact.label}:</span>
                  <a
                    href={contact.href}
                    target={contact.href.startsWith('http') ? '_blank' : undefined}
                    rel={contact.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-slate-700 hover:text-slate-900 font-bold border-b border-transparent hover:border-slate-800 transition-colors"
                  >
                    {contact.value}
                  </a>
                </div>
              ))}
            </div>

            <div className="text-center md:text-left">
              <Link
                href={content.cta.topup_href}
                className="inline-flex items-center justify-center px-8 py-4 bg-[#548DC8] hover:bg-[#4377B0] text-white font-mono text-xs uppercase tracking-widest font-bold transition-all shadow-sm rounded-xl"
              >
                {isSi ? content.cta.topup_cta_si : content.cta.topup_cta_en}
              </Link>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer locale={activeLang} />
    </div>
  );
}
